import React, { useEffect, useState } from 'react';
import { CustomerCard } from './CustomerCard';
import { useCustomerSearchWithQuery } from '../../hooks/useCustomerSearchWithQuery';
import { Alert, SearchBar, LoadingSpinner, Button } from '@shared/components';
import pluralise from '@/app/utils/formatPluralise';
import type { Customer } from '../../types/customers.types';
import { NewCustomerForm } from '../NewCustomerForm/NewCustomerForm';
import { PermissionGuard, usePermissions } from '@/features/auth';

type ViewMode = 'search' | 'createNew';

interface CustomerSearchProps {
    onSelectCustomer: (customer: Customer | null) => void;
    selectedCustomer?: Customer | null; // Optional prop to allow external selection
}

export const CustomerSearch: React.FC<CustomerSearchProps> = ({
    onSelectCustomer,
    selectedCustomer: passedInCustomer,
}) => {
    // State management
    const [currentView, setCurrentView] = useState<ViewMode>('search');

    // Custom hooks
    const {
        customers,
        searchTerm,
        debouncedSearchTerm,
        appliedSearchTerm,
        setSearchTerm,
        selectedCustomer,
        selectCustomer,
        clearSelection,
        resetSearch,
        isLoading,
        isFetching,
        error,
        totalCustomers,
        filteredCount,
    } = useCustomerSearchWithQuery();

    const { canAdd } = usePermissions();
    const canAddCustomer = canAdd('customer');

    useEffect(() => {
        if (!canAddCustomer && currentView === 'createNew') {
            setCurrentView('search');
        }
    }, [canAddCustomer, currentView]);

    // Computed values
    const isShowingCreateForm = currentView === 'createNew';
    const isSearchPending = searchTerm !== debouncedSearchTerm && searchTerm.length >= 3;

    // View management functions
    const showSearchInterface = () => setCurrentView('search');
    const toggleView = () => setCurrentView(currentView === 'search' ? 'createNew' : 'search');

    // Event handlers

    const handleSelectCustomer = (customer: Customer) => {
        selectCustomer(customer);
        onSelectCustomer(customer);
    };

    const handleClearSelection = () => {
        clearSelection();
        onSelectCustomer(null);
    };

    const handleNewCustomerSubmit = (createdCustomer: Customer) => {
        // After creating a new customer, select them and return to search view
        handleSelectCustomer(createdCustomer);
        showSearchInterface();
    };

    // Helper functions for rendering different states
    const renderNoSearchMessage = () => (
        <Alert variant="info" role="note">
            <div className="flex flex-col items-start gap-2">
                <p className="font-semibold">Search for a Customer</p>
                <p>Enter at least 3 characters to search for existing customers.</p>
                <p>Search by name, email, customer reference (CUST-XXXXXX), postcode, or address.</p>
            </div>
        </Alert>
    );

    const renderNoResultsMessage = () => (
        <Alert variant="warning" role="status">
            <div className="flex flex-col items-start gap-2">
                <p className="font-semibold">No Customers Found</p>
                <p>
                    No customers match your search:{' '}
                    <span className="font-semibold">"{appliedSearchTerm}"</span>
                </p>
                <p>
                    {canAddCustomer ? (
                        <>
                            Try adjusting your search terms or{' '}
                            <Button
                                onClick={toggleView}
                                size="none"
                                variant="clear"
                                className="font-semibold underline"
                            >
                                create a new customer.
                            </Button>
                        </>
                    ) : (
                        <>Try adjusting your search terms.</>
                    )}
                </p>
                <button onClick={resetSearch} className="underline">
                    Clear search
                </button>
            </div>
        </Alert>
    );

    const renderErrorMessage = () => (
        <Alert variant="warning" role="status">
            <div className="flex flex-col items-start gap-2">
                <p className="font-semibold">No Customers Found</p>
                <p>
                    Unfortunately, there are no customers available at the moment from your search term:{' '}
                    <span className="font-semibold">{searchTerm}</span>.
                </p>
                <p>Please check back later or contact support if you believe this is an error.</p>
                <button onClick={resetSearch} className="underline">
                    Clear search
                </button>
            </div>
        </Alert>
    );

    const renderCustomerGrid = () => (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-3 lg:gap-5">
            {customers.map((customer) => (
                <CustomerCard
                    key={customer.id}
                    customer={customer}
                    className="w-full"
                    onSelectCustomer={handleSelectCustomer}
                />
            ))}
        </div>
    );

    const renderSearchResults = () => {
        if (error) {
            return renderErrorMessage();
        }

        if (customers.length > 0) {
            return renderCustomerGrid();
        }

        if (appliedSearchTerm) {
            return renderNoResultsMessage();
        }

        return renderNoSearchMessage();
    };

    const renderSelectedCustomer = () => {
        if (passedInCustomer || !selectedCustomer) return null;

        return (
            <Alert variant="success" role="status">
                <div className="grow">
                    <h3 className="font-semibold">Selected Customer</h3>
                    <p>
                        {selectedCustomer.forename} {selectedCustomer.surname}
                    </p>
                </div>
                <button onClick={handleClearSelection}>Clear Selection</button>
            </Alert>
        );
    };

    return (
        <div className="space-y-6">
            {/* Search Input */}
            <div className="flex flex-wrap justify-between content-center">
                <div className="relative max-w-md flex-grow">
                    <SearchBar
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        placeholder="Search by name, email, reference, postcode... (min 3 chars)"
                    />
                    {/* Show search state indicator */}
                    {isSearchPending && (
                        <LoadingSpinner
                            size="sm"
                            decorative
                            className="absolute right-3 top-1/2 transform -translate-y-1/2"
                        />
                    )}
                    {isFetching && !isSearchPending && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <div className="animate-pulse rounded-full h-4 w-4 bg-mp-primary"></div>
                        </div>
                    )}
                </div>
                <div className="flex-shrink-0">
                    {isShowingCreateForm ? (
                        <Button onClick={toggleView} size="sm">Back to Search</Button>
                    ) : (
                        <PermissionGuard permission={{ resource: 'customer', action: 'add' }}>
                            <Button onClick={toggleView} size="sm">New Customer</Button>
                        </PermissionGuard>
                    )}
                </div>
            </div>

            {/* Selected Customer Display */}
            {renderSelectedCustomer()}

            {isShowingCreateForm ? (
                <NewCustomerForm onSubmit={handleNewCustomerSubmit} mode="deferred" />
            ) : (
                <>
                    {/* Results Count */}
                    {searchTerm && (
                        <div className="text-sm text-mp-muted">
                            Showing {filteredCount} of {pluralise('customer', totalCustomers, true)}
                        </div>
                    )}

                    {/* Loading State */}
                    {isLoading ? (
                        <div className="flex justify-center py-10">
                            <LoadingSpinner size="md" className="p-4" />
                        </div>
                    ) : (
                        renderSearchResults()
                    )}
                </>
            )}
        </div>
    );
};
